import {
  Node
} from '../merkle_tree.js';
import {
  isAtom,
  isObject,
  isArray,
} from '../../../helper/index.js';
import {
  getItems
} from '../../../json_path.js';

// jsonToMerkleTree convert a json value to a merkle tree
const jsonToMerkleTree = (json, hashFn) => {
  if (isAtom(json)) {
    return new Node({
      type: 'atom',
      value: json,
    }, hashFn);
  }

  if (isArray(json)) {
    const node = new Node({
      type: 'array',
    }, hashFn);

    for (let i = 0; i < json.length; i++) {
      node.appendChild(jsonToMerkleTree(json[i], hashFn));
    }

    return node;
  }

  if (isObject(json)) {
    // TODO sort by key
    const {
      items,
      keyMap
    } = getObjectItems(json);

    const node = new Node({
      type: 'object',
      keyMap,
    }, hashFn);

    for (let i = 0; i < items.length; i += 2) {
      // pair key, value
      node.appendChild(jsonToMerkleTree(items[i], hashFn));
      node.appendChild(jsonToMerkleTree(items[i + 1], hashFn));
    }

    return node;
  }

  throw new Error(`${json} is not json type`);
};

const getObjectItems = (obj) => {
  const list = [];
  for (let key in obj) {
    list.push({
      key,
      value: obj[key]
    });
  }
  list.sort((a, b) => {
    return a.key > b.key;
  });

  const items = [],
    keyMap = {}; // key map store key-index

  for (let i = 0; i < list.length; i++) {
    const {
      key,
      value
    } = list[i];

    items.push(key);
    keyMap[key] = items.length - 1;
    items.push(value);
  }

  return {
    items,
    keyMap
  };
};

const findNodeByJsonPath = function(n, jsonPath) {
  const {
    has,
    node,
    path
  } = hasNodeByJsonPath(n, jsonPath);

  if (!has) {
    throw new Error(`missing node for path ${jsonPath}`);
  }

  return {
    node,
    path
  };
};

const hasNodeByJsonPath = function(node, jsonPath) {
  const keys = getItems(jsonPath);
  let cur = node;
  const path = [];

  for (let i = 0; i < keys.length; i++) {
    if (!cur) {
      return false;
    }
    const key = keys[i];

    let index = null;

    switch (cur.data.type) {
      case 'atom':
        return {
          has: false
        };

      case 'array':
        index = Number(key);
        if (isNaN(index)) {
          return {
            has: false
          };
        }
        path.push(index);
        cur = cur.getChildByIndex(index);
        break;

      case 'object':
        // get key index
        index = Number(cur.data.keyMap[key]);
        if (isNaN(index)) {
          return {
            has: false
          };
        }
        path.push(index + 1);
        cur = cur.getChildByIndex(index + 1);
        break;

      default:
        return {
          has: false
        };
    }
  }

  return {
    has: true,
    node: cur,
    path
  };
};

const merkleTreeToJson = (node) => {
  let ans = {},
    items = [];

  switch (node.data.type) {
    case 'atom':
      return node.data.value;
    case 'array':
      return node.children.map(merkleTreeToJson);
    case 'object':
      ans = {};
      items = node.children.map(merkleTreeToJson);
      for (let i = 0; i < items.length; i += 2) {
        ans[items[i]] = items[i + 1];
      }
      return ans;
    default:
      throw new Error(`unexpected data type ${node.data.type}`);
  }
};

export {
  jsonToMerkleTree,
  merkleTreeToJson,
  findNodeByJsonPath,
  hasNodeByJsonPath,
};
